import {
  ActionName,
  ActionProcessMode,
  CategoryName,
  SignActionP,
  SignatureType,
} from '@infotech-soe/metaschema';
import { DigitalSignService } from '@infotech-soe/mia-ds-service';
import {
  IDigitalSignService,
  SignCredentials,
} from '@infotech-soe/mia-ds-service/types';
import { decode } from 'base64-arraybuffer';

import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import CategoryViewModel from '../../category/view-model/CategoryViewModel';
import { ClientContext } from '../../context/IContextService';
import { ActionWithNextAction, SignActionArgs } from '../types';
import { UserInteractor, UserInteractorType } from '../UserInteractor';
import { RefreshStrategy } from '../wrap/CategoryCommand';

import RunActionCommandImpl from './RunActionCommandImpl';

const SIGN_ACTION_ARG_NAME = 'document';
const SIGN_ACTION_SIGNATURE_TYPE_ARG_NAME = 'signatureType';

class SignCommandImpl extends RunActionCommandImpl {
  private readonly signService: IDigitalSignService;
  constructor(
    protected category: CategoryName,
    protected actionName: ActionName,
    protected actionSchema: SignActionP,
    protected categoryVM: CategoryViewModel
  ) {
    super(category, actionName, actionSchema, categoryVM);
    this.signService = new DigitalSignService();
  }

  public async getInitialArgs(): Promise<{
    hasStoredCredentials: boolean;
    signatureType: SignatureType;
  }> {
    return {
      hasStoredCredentials: this.signService.hasStoredCredentials,
      signatureType:
        this.actionSchema.Args[SIGN_ACTION_SIGNATURE_TYPE_ARG_NAME].literal ??
        'detached',
    };
  }

  public async execute(
    context: ClientContext,
    args: Partial<SignActionArgs>
  ): Promise<void | ActionWithNextAction> {
    const apiProvider = new MerpsApiProvider();
    const signatureType =
      this.actionSchema.Args[SIGN_ACTION_SIGNATURE_TYPE_ARG_NAME].literal ??
      'detached';

    let signData: string | ArrayBuffer;

    if ('documentRoutine' in this.actionSchema.Args) {
      const routineName = this.actionSchema.Args.documentRoutine.literal;
      const result = await apiProvider.actions.executeAction(
        routineName,
        context.toServerContext(),
        {}
      );

      if (typeof result !== 'string') {
        throw new TypeError(
          `Expected documentRoutine "${routineName}" to return a string, but got: "${JSON.stringify(result)}"`
        );
      }

      signData = result;
    } else {
      const serverArgs = await apiProvider.actions.getActionArgs(
        this.actionName,
        context.toServerContext()
      );

      const document = serverArgs[SIGN_ACTION_ARG_NAME];

      if (!document) {
        return;
      }

      const streamId = document.id;

      if (!streamId && typeof document !== 'string') {
        throw new TypeError(
          `Expected to get "{ ${SIGN_ACTION_ARG_NAME}: { id: string } }" object or string, but got: "${JSON.stringify(
            serverArgs
          )}"`
        );
      }

      signData = streamId ? await apiProvider.streams.read(streamId) : document;
    }

    await this.signService.initialize(args.credentials?.dsAgent);
    const signedData = await this.signService.sign(
      args.credentials as SignCredentials,
      signatureType,
      signData,
      args.saveCredentials
    );

    const signedDataId = await apiProvider.streams.write(decode(signedData));
    const documentArg =
      typeof signData === 'string'
        ? signData
        : { id: await apiProvider.streams.write(signData) };

    return apiProvider.actions.executeAction(
      this.action,
      context.toServerContext(),
      {
        document: documentArg,
        signature: { id: signedDataId },
        signatureType,
      }
    );
  }

  public getName(): string {
    return super.getName();
  }

  public getProcessMode(): ActionProcessMode {
    return this.actionSchema.ProcessMode;
  }

  public getUserInteractor(): UserInteractor {
    return { type: UserInteractorType.SIGN_FORM };
  }

  public getRefreshStrategy(): RefreshStrategy {
    switch (this.actionSchema.RefreshMode) {
      case 'Current':
        return RefreshStrategy.CURRENT;
      case 'None':
        return RefreshStrategy.NONE;
    }
  }
}

export default SignCommandImpl;
